'use es6';

import styled from 'styled-components';
import { bender } from 'legacy-hubspot-bender-context';
import { Fragment, useCallback, useEffect, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import EPHeader from 'editor-components/header/EPHeader';
import EPNavigationBar from 'editor-components/header/EPNavigationBar';
import UsageTracker, { useTracker } from 'ContentEditorUI/utils/UsageTracker';
import { BATTLESHIP } from 'HubStyleTokens/colors';
import UIButton from 'UIComponents/button/UIButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import TranslationSelectorContainer from 'ContentEditorUI/containers/TranslationSelectorContainer';
import PageAbControlsContainer from 'page-editor-ui/containers/PageAbControlsContainer';
import PageMABControlsContainer from 'page-editor-ui/containers/PageMABControlsContainer';
import BreakpointsContainer from 'ContentEditorUI/containers/BreakpointsContainer';
import ExitButton from 'ContentEditorUI/components/header/ExitButton';
import PagePublishButtonContainer from 'page-editor-ui/containers/PagePublishButtonContainer';
import PatagoniaPagePublishButtonContainer from 'page-editor-ui/containers/PatagoniaPagePublishButtonContainer';
import ExitDeveloperModeButton from 'ContentEditorUI/components/ExitDeveloperModeButton';
import { getHasUnsavedChanges } from 'ContentEditorUI/redux/selectors/saveDataSelectors';
import { getDashboardUrl } from 'page-editor-ui/redux/selectors/urlSelectors';
import { getIsInDeveloperMode, getIsReadOnlyMode } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import { getHasUndoRedoAccess } from 'ContentEditorUI/redux/selectors/moduleSelectors';
import { getCanUseQuickSpot, getIsUngatedForUnifiedPublishButton } from 'ContentEditorUI/redux/selectors/authSelectors';
import { showLoading, hideLoading, openModal } from 'ContentEditorUI/redux/actions/appActions';
import { save } from 'page-editor-ui/redux/actions/pageSaveActions';
import { routeToPage } from 'ContentEditorUI/redux/actions/routingActions';
import { setShouldClearHeaderActiveItem, updateHeaderActiveItem } from 'ContentEditorUI/redux/actions/headerActions';
import { getShouldClearHeaderActiveItem } from 'ContentEditorUI/redux/selectors/headerSelectors';
import EPSettingsButton from 'editor-components/header/EPSettingsButton';
import EPSpotlightButton from 'editor-components/header/EPSpotlightButton';
import UndoRedoButtonContainer from 'ContentEditorUI/containers/UndoRedoButtonContainer';
import UIFlex from 'UIComponents/layout/UIFlex';
import { getShouldUseResponsiveBreakpoints } from 'ContentEditorUI/redux/selectors/styleBreakpointsSelectors';
import { useNavigationGroups } from 'page-editor-ui/hooks/useNavigationGroups';
import PageSaveButtonContainer from './PageSaveButtonContainer';
import { MODAL_TYPES } from 'ContentEditorUI/lib/modalConstants';
import UIAutosizedTextInput from 'UIComponents/input/UIAutosizedTextInput';
import { getContentModelName, getPublishDate, getContentModelPageExpiryDate } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { updateName } from 'ContentEditorUI/redux/actions/baseContentModelActions';
import useSetSettingsModalOpenWithNavItem from 'page-editor-ui/hooks/useSetSettingsModalOpenWithNavItem';
import ContentPublishStatus from 'ContentEditorUI/components/shared/ContentPublishStatus';
import { getHardSavedContentModelIsPublishedNotScheduled, getHardSavedContentModelIsScheduled, getHardSavedContentModelScheduledUpdateDate } from 'ContentEditorUI/redux/selectors/hardSavedContentModelSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TabStartSlotWrapper = styled.div.withConfig({
  displayName: "PatagoniaHeaderContainer__TabStartSlotWrapper",
  componentId: "sc-1h4zkhu-0"
})([".tab-slot-start-flex{span{font-weight:normal;}& > *:first-child{padding-right:10px;border-right:1px solid ", ";}& > *:nth-child(2) button{margin-left:10px !important;}& > *:only-child{border-right:none;}}"], BATTLESHIP);
const EditorToolbarEndSlot = () => {
  const dispatch = useDispatch();
  const hasUnsavedChanges = useSelector(getHasUnsavedChanges);
  const isInDeveloperMode = useSelector(getIsInDeveloperMode);
  const handleSaveClick = useCallback(() => {
    dispatch(save());
    UsageTracker.trackEditorInteraction({
      action: 'Revisions Action',
      event: 'clicked-save'
    });
  }, [dispatch]);
  const handlePreviewClick = useCallback(() => {
    if (hasUnsavedChanges) {
      dispatch(showLoading());
      dispatch(save()).finally(() => {
        dispatch(hideLoading());
        dispatch(routeToPage('preview'));
      }).catch(error => console.error(error));
    } else {
      dispatch(routeToPage('preview'));
    }
  }, [dispatch, hasUnsavedChanges]);
  const isUngatedForUnifiedPublishButton = useSelector(getIsUngatedForUnifiedPublishButton);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isInDeveloperMode && /*#__PURE__*/_jsx(ExitDeveloperModeButton, {}), /*#__PURE__*/_jsx(PageSaveButtonContainer, {
      onSaveClick: handleSaveClick
    }), /*#__PURE__*/_jsx(UIButton, {
      "data-onboarding": "ep-navigation-preview",
      "data-campaign-assistant-onboarding-tour": "tour-step-preview",
      onClick: handlePreviewClick,
      use: "secondary-ghost",
      size: "small",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "components.tabBar.preview"
      })
    }), isUngatedForUnifiedPublishButton ? /*#__PURE__*/_jsx(PagePublishButtonContainer, {}) : /*#__PURE__*/_jsx(PatagoniaPagePublishButtonContainer, {})]
  });
};
const PatagoniaHeaderContainer = /*#__PURE__*/memo(() => {
  const dispatch = useDispatch();
  const dashboardUrl = useSelector(getDashboardUrl);
  const hasUndoRedoAccess = useSelector(getHasUndoRedoAccess);
  const isReadOnlyMode = useSelector(getIsReadOnlyMode);
  const shouldUseResponsiveBreakpoints = useSelector(getShouldUseResponsiveBreakpoints);
  const shouldClearHeader = useSelector(getShouldClearHeaderActiveItem);
  const pageName = useSelector(getContentModelName);
  const tracker = useTracker();
  const navigationGroups = useNavigationGroups();
  const canUseQuickSpot = useSelector(getCanUseQuickSpot);
  const HEADER_CLASSNAME = 'fullscreen-editor-header';
  const isUngatedForUnifiedPublishButton = useSelector(getIsUngatedForUnifiedPublishButton);
  const handleQuickSpotOpenClick = useCallback(() => {
    dispatch(openModal(MODAL_TYPES.SPOTLIGHT));
  }, [dispatch]);
  useEffect(() => {
    // Show alternate header styling for branch previews
    if (bender.staticDomainPrefix.includes('dynamic.hsappstatic.net')) {
      const headerElement = document.querySelector(`.${HEADER_CLASSNAME}`);
      if (headerElement) {
        headerElement.classList.add('hubspot-branch-preview');
      }
    }
  }, []);
  const handleActiveItemChange = useCallback(activeItem => {
    dispatch(updateHeaderActiveItem(activeItem));
    if (activeItem) {
      dispatch(setShouldClearHeaderActiveItem(false));
    }
  }, [dispatch]);
  const setSettingsModalOpenWithNavItem = useSetSettingsModalOpenWithNavItem(MODAL_TYPES.SETTINGS_MODAL);
  const handleChangeName = e => dispatch(updateName(e.target.value));
  const isScheduled = useSelector(getHardSavedContentModelIsScheduled);
  const isPublished = useSelector(getHardSavedContentModelIsPublishedNotScheduled);
  const scheduledUpdateDate = useSelector(getHardSavedContentModelScheduledUpdateDate);
  const publishDate = useSelector(getPublishDate);
  const unpublishDate = useSelector(getContentModelPageExpiryDate);
  return /*#__PURE__*/_jsx(EPHeader, {
    editorToolbarClassName: HEADER_CLASSNAME,
    tabsClassName: "m-y-2",
    titleText: /*#__PURE__*/_jsxs(UIFlex, {
      align: "center",
      justify: "center",
      direction: "column",
      children: [/*#__PURE__*/_jsx(UIAutosizedTextInput, {
        affordance: true,
        onChange: handleChangeName,
        use: "on-dark",
        value: pageName,
        readOnly: isReadOnlyMode,
        style: {
          margin: 'auto'
        }
      }), isUngatedForUnifiedPublishButton ? /*#__PURE__*/_jsx(ContentPublishStatus, {
        isPublished: isPublished,
        isScheduled: isScheduled,
        publishDate: publishDate,
        scheduledUpdateDate: scheduledUpdateDate,
        unpublishDate: unpublishDate
      }) : null]
    }),
    editorToolbarStartSlot: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ExitButton, {
        dashboardUrl: dashboardUrl
      }), /*#__PURE__*/_jsx(EPNavigationBar, {
        navigationGroups: navigationGroups,
        onActiveItemChange: handleActiveItemChange,
        shouldClear: shouldClearHeader
      })]
    }),
    editorToolbarEndSlot: /*#__PURE__*/_jsx(EditorToolbarEndSlot, {}),
    tabsStartSlot: /*#__PURE__*/_jsx(TabStartSlotWrapper, {
      children: /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        justify: "start",
        className: "tab-slot-start-flex",
        children: [/*#__PURE__*/_jsx(TranslationSelectorContainer, {}), /*#__PURE__*/_jsx(PageMABControlsContainer, {}), /*#__PURE__*/_jsx(PageAbControlsContainer, {})]
      })
    }),
    tabsMiddleSlot: /*#__PURE__*/_jsx(Fragment, {
      children: shouldUseResponsiveBreakpoints && /*#__PURE__*/_jsx(BreakpointsContainer, {})
    }),
    tabsEndSlot: /*#__PURE__*/_jsxs(UIFlex, {
      align: "center",
      justify: "end",
      children: [canUseQuickSpot && tracker && /*#__PURE__*/_jsx(EPSpotlightButton, {
        onClick: handleQuickSpotOpenClick,
        usageTracker: tracker
      }), tracker && /*#__PURE__*/_jsx(EPSettingsButton, {
        onClick: () => setSettingsModalOpenWithNavItem('general'),
        usageTracker: tracker
      }), /*#__PURE__*/_jsx(NavMarker, {
        name: "PARENT_APP_CHROME_RENDERED"
      }), hasUndoRedoAccess && /*#__PURE__*/_jsx(UndoRedoButtonContainer, {
        className: "m-left-2",
        isVisible: true,
        disabled: isReadOnlyMode
      })]
    })
  });
});
export default PatagoniaHeaderContainer;